import { Injectable, Inject, PLATFORM_ID, signal, WritableSignal } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { Router } from "@angular/router";
import { User } from "../models/user";
import { environment } from "src/environments/environment";
import { MBService } from '../../core/service/msg_box.service';

@Injectable({
  providedIn: "root",
})
export class AuthService {
  public currentUser: WritableSignal<User | null> = signal(null);

  constructor(
    private http: HttpClient,
    private msgBox: MBService,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    let initialUser = null;
    if (isPlatformBrowser(this.platformId)) {
      const storedUser = sessionStorage.getItem("currentUser");
      if (storedUser) {
        initialUser = JSON.parse(storedUser);
      }
    }
    this.currentUser.set(initialUser);
  }

  public get currentUserValue(): User | null {
    return this.currentUser();
  }

  /** Devuelve la ruta de navegación según el rol del usuario */
  getRutaPorRol(role: string): string {
    switch (role) {
      case "Doctor":        return "/medicos/dashboard";
      case "Empresas":      return "/empresas/dashboard";
      case "Paciente":      return "/pacientes/dashboard";
      case "Representantes":return "/representantes/dashboard";
      case "Admin":         return "/intranet/dashboard";
      default:              return "/authentication/signin";
    }
  }

  /** Guarda la sesión en storage y actualiza el Subject */
  private _guardarSesion(user: User, token: string): void {
    (user as any).Token = token;
    const liga = this.getRutaPorRol((user as any).Role).split('/')[1];
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem("liga", liga);
      localStorage.setItem("choose_skin", "theme-blue");
      localStorage.setItem("choose_skin_active", "blue");
      sessionStorage.setItem("currentUser", JSON.stringify(user));
    }
    this.currentUser.set(user);
  }

  /** Login por PIN + Fecha (pacientes via código de expediente) */
  Validar(user: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/validar`, user)
      .pipe(map((res) => {
        if (res?.token) {
          // El servidor incluye role='Paciente' en el JWT; también viene en user.Role
          if (!res.user.Role) res.user.Role = "Paciente"; // fallback por compatibilidad
          this._guardarSesion(res.user, res.token);
          return res.user;
        } else {
          this.msgBox.OcurrioError('PIN no Válido *');
        }
      }));
  }

  /** Login por Email + Contraseña (médicos, empresas, admin) */
  login(username: string, password: string): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/authenticate`, { username, password })
      .pipe(map((res) => {
        // La validación de contraseña ocurre completamente en el servidor
        // Si el servidor responde 200, las credenciales son correctas
        if (res?.token) {
          this._guardarSesion(res.user, res.token);
          return res.user;
        }
      }));
  }

  /** Login por Expediente ID + Fecha */
  loginExp(id: string, fecha: string): Observable<any> {
    return this.http
      .post<any>(`${environment.apiUrl}/expediente`, { id, fecha })
      .pipe(map((res) => {
        if (res?.token) {
          if (!res.user.Role) res.user.Role = "Paciente"; // fallback por compatibilidad
          this._guardarSesion(res.user, res.token);
          return res.user;
        }
      }));
  }

  logout() {
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.removeItem("liga");
      localStorage.removeItem("choose_skin");
      localStorage.removeItem("choose_skin_active");
      sessionStorage.removeItem("currentUser");
      localStorage.removeItem("isRtl");
      this.router.navigate(["/authentication/signin"]);
    }
    this.currentUser.set(null);
    return of({ success: true });
  }

  getToken(): string {
    if (isPlatformBrowser(this.platformId)) {
      return localStorage.getItem('currentUser.token');
    }
    return null;
  }
}
